/**
 * Trovva — 3-Pane Contact Detail View
 *
 * FUB-style layout:
 *   LEFT   → Contact info, stage pipeline, tags, relationships, lender
 *   CENTER → Unified communication timeline + inline composers (SMS, note, email)
 *   RIGHT  → Tasks, deals, action plans, AI follow-up
 *
 * Replaces the drawer when a lead card is clicked. Can also be rendered
 * as a slide-over panel within LeadsView.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Phone, PhoneCall, PhoneOff, MessageSquare, Mail, Edit3, Send, Loader2,
  Plus, CheckCircle2, FileText, User, UserPlus, Clock, ArrowRight,
  Calendar, DollarSign, Tag as TagIcon, AlertCircle, Briefcase, Sparkles,
  Star, StarOff, Link2, Play, Pause, Volume2, ChevronDown, Users,
  Mic, MicOff, Hash, ChevronLeft, ChevronRight, Globe, Home,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { formatDate, formatDateTime, formatDateRelative } from '../../lib/dateFormat';
import { computeLeadScore, stalenessOf, daysSinceContact } from '../../lib/leadScoring';
import StageGateModal from './StageGateModal';
import AIFollowUpModal from '../crm/AIFollowUpModal';
import EnrollActionPlanModal from '../crm/EnrollActionPlanModal';
import WebActivityPanel from './WebActivityPanel';
import { missingForStage } from '../../lib/stageGating';

// ── Responsive Hook ──────────────────────────────────────────

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

// ── Constants ─────────────────────────────────────────────────

const LEAD_STATUSES = [
  // Sales funnel (pre-contract) — bold, distinguishable colors
  { value: 'new',              label: 'New',              color: '#6366F1' },   // Indigo — fresh
  { value: 'contacted',        label: 'Contacted',        color: '#F59E0B' },   // Amber — warm, active
  { value: 'qualified',        label: 'Qualified',        color: '#0EA5E9' },   // Sky blue — progressing
  { value: 'active_search',    label: 'Active search',    color: '#2563EB' },   // Bold blue — in motion
  { value: 'offer_submitted',  label: 'Offer submitted',  color: '#8B5CF6' },   // Violet — escalating
  { value: 'offer_accepted',   label: 'Offer accepted',   color: '#16A34A' },   // Bold green — success
  // Terminal
  { value: 'lost',             label: 'Lost',             color: '#DC2626' },   // Strong red
];

const LEAD_TYPES = ['buyer', 'seller', 'renter', 'commercial', 'pm_owner', 'investor'];

const RELATIONSHIP_TYPES = ['spouse', 'parent', 'child', 'referrer', 'referral', 'partner', 'colleague', 'other'];

const DISPOSITIONS = [
  { value: 'spoke_with_contact', label: 'Spoke with contact' },
  { value: 'left_voicemail', label: 'Left voicemail' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'busy', label: 'Busy' },
];

// ── Main Component ────────────────────────────────────────────

export default function ContactDetailView({ leadId, onClose, onChange, demoMode, showToast }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [stageGate, setStageGate] = useState(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showEnrollPlan, setShowEnrollPlan] = useState(false);

  // Mobile responsiveness
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState('timeline');

  // Timeline refresh trigger
  const [timelineKey, setTimelineKey] = useState(0);
  const refreshTimeline = () => setTimelineKey(k => k + 1);

  const loadLead = useCallback(async () => {
    if (!leadId) return;
    setLoading(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch(`/api/leads?id=${leadId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Failed (${r.status})`);
      setLead(data.lead);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { loadLead(); }, [loadLead]);

  const updateLead = async (patch) => {
    if (!lead) return;
    if (patch.lead_status && patch.lead_status !== lead.lead_status) {
      const missing = missingForStage({ ...lead, ...patch }, patch.lead_status);
      if (missing.length > 0) {
        setStageGate({ targetStatus: patch.lead_status, originalPatch: patch });
        return;
      }
    }
    setSaving(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/leads?id=${lead.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Update failed');
      setLead(data.lead);
      onChange?.(data.lead);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) { setError(e.message); showToast?.(e.message); }
    finally { setSaving(false); }
  };

  const commitPatch = async (patch) => {
    setSaving(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/leads?id=${lead.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Update failed');
      setLead(data.lead);
      onChange?.(data.lead);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); setStageGate(null); }
  };

  // ── Style helpers (defined before first return to avoid hoisting issues in bundlers) ──
  function containerStyle() {
    return { display: 'flex', flexDirection: 'column', height: '100%', background: theme.colors.card, borderRadius: 0, overflow: 'hidden' };
  }
  function topBarBtn() {
    return { background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, borderRadius: 8, width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
  }
  function avatarStyle(size = 36) {
    return { width: size, height: size, borderRadius: size / 2, flexShrink: 0, background: theme.colors.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.35, fontWeight: 700 };
  }

  if (loading) {
    return (
      <div style={containerStyle()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.colors.textLight, gap: 8 }}>
          <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading contact…
        </div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div style={containerStyle()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 8 }}>
          <AlertCircle size={20} color={theme.colors.danger} />
          <span style={{ color: theme.colors.danger, fontSize: 13 }}>{error || 'Contact not found'}</span>
          <button onClick={onClose} style={{ ...btnPrimary(), marginTop: 8 }}>Close</button>
        </div>
      </div>
    );
  }

  const score = computeLeadScore(lead);
  const staleness = stalenessOf(lead);
  const daysSince = daysSinceContact(lead);
  const initials = `${(lead.first_name || '')[0] || ''}${(lead.last_name || '')[0] || ''}`.toUpperCase();
  const statusMeta = LEAD_STATUSES.find(s => s.value === lead.lead_status) || LEAD_STATUSES[0];

  return (
    <div style={containerStyle()}>
      {/* ── Top bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '8px 12px' : '10px 16px',
        borderBottom: `1px solid ${theme.colors.border}`,
        background: theme.colors.card, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10 }}>
          <div style={avatarStyle(isMobile ? 28 : 36)}>{initials}</div>
          {!isMobile && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>
                  {lead.first_name} {lead.last_name}
                </span>
                {score != null && <ScoreChip score={score} />}
                <StageBadge statusMeta={statusMeta} />
                {staleness && <StalenessBadge staleness={staleness} days={daysSince} />}
              </div>
              <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 1 }}>
                {lead.lead_type && <span style={{ textTransform: 'capitalize' }}>{lead.lead_type}</span>}
                {lead.lead_source && <span> · {lead.lead_source}</span>}
                {lead.email && <span> · {lead.email}</span>}
              </div>
            </div>
          )}
          {isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>
                {lead.first_name} {lead.last_name}
              </span>
              <span style={{ fontSize: 10, color: theme.colors.textLight }}>
                {lead.lead_type && `${lead.lead_type}`}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: isMobile ? 4 : 6, alignItems: 'center' }}>
          {savedFlash && !isMobile && <span style={{ fontSize: 11, color: theme.colors.success, fontWeight: 600 }}>Saved ✓</span>}
          {saving && <Loader2 size={14} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />}
          {!isMobile && (
            <>
              <button onClick={() => setShowFollowUp(true)} style={topBarBtn()} title="AI Follow-Up">
                <Sparkles size={14} color={theme.colors.accent} />
              </button>
              <button onClick={() => setShowEnrollPlan(true)} style={topBarBtn()} title="Enroll in Action Plan">
                <Calendar size={14} />
              </button>
            </>
          )}
          <button onClick={onClose} style={topBarBtn()}><X size={isMobile ? 14 : 16} /></button>
        </div>
      </div>

      {/* ── Layout: Desktop (3-pane) or Mobile (tabbed) ── */}
      {isMobile ? (
        <MobileLayout
          lead={lead}
          statusMeta={statusMeta}
          timelineKey={timelineKey}
          mobileTab={mobileTab}
          setMobileTab={setMobileTab}
          onUpdate={updateLead}
          refreshTimeline={refreshTimeline}
          showToast={showToast}
        />
      ) : (
        <div style={{
          display: 'flex', flex: 1, overflow: 'hidden',
        }}>
          {/* LEFT PANE — Contact Info */}
          <LeftPane lead={lead} statusMeta={statusMeta} onUpdate={updateLead} showToast={showToast} />

          {/* CENTER PANE — Communication Timeline */}
          <CenterPane
            lead={lead}
            timelineKey={timelineKey}
            refreshTimeline={refreshTimeline}
            showToast={showToast}
          />

          {/* RIGHT PANE — Tasks, Deals, Plans */}
          <RightPane lead={lead} showToast={showToast} refreshTimeline={refreshTimeline} updateLead={updateLead} />
        </div>
      )}

      {/* Modals */}
      {stageGate && (
        <StageGateModal
          lead={lead}
          targetStatus={stageGate.targetStatus}
          onCancel={() => setStageGate(null)}
          onConfirm={async (extraFields) => {
            await commitPatch({ ...stageGate.originalPatch, ...extraFields });
          }}
        />
      )}
      {showFollowUp && <AIFollowUpModal contact={lead} onClose={() => setShowFollowUp(false)} onSent={() => { setShowFollowUp(false); refreshTimeline(); showToast?.('Follow-up sent!'); }} />}
      {showEnrollPlan && <EnrollActionPlanModal leadId={lead.id} onClose={() => setShowEnrollPlan(false)} showToast={showToast} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MOBILE LAYOUT — Tabbed interface
// ══════════════════════════════════════════════════════════════

function MobileLayout({ lead, statusMeta, timelineKey, mobileTab, setMobileTab, onUpdate, refreshTimeline, showToast }) {
  const tabs = [
    { key: 'info', label: 'Info' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'tasks', label: 'Tasks' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', background: theme.colors.bg }}>
      {/* Mobile tab bar */}
      <div style={{
        display: 'flex', gap: 6, padding: '10px 12px',
        background: theme.colors.card, borderBottom: `1px solid ${theme.colors.border}`,
        overflowX: 'auto', flexShrink: 0, alignItems: 'center',
        position: 'relative',
      }}>
        {/* Left swipe hint */}
        {mobileTab !== 'info' && (
          <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
            <ChevronLeft size={16} color={theme.colors.textLight} style={{ opacity: 0.5 }} />
          </div>
        )}

        {tabs.map(t => {
          const active = mobileTab === t.key;
          return (
            <button key={t.key} onClick={() => setMobileTab(t.key)} style={{
              padding: '8px 16px', borderRadius: 20,
              border: active ? `1.5px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
              background: active ? `${theme.colors.primary}10` : 'transparent',
              cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 500,
              color: active ? theme.colors.primary : theme.colors.textLight,
              whiteSpace: 'nowrap', transition: 'all 0.15s', flexShrink: 0,
            }}>
              {t.label}
            </button>
          );
        })}

        {/* Right swipe hint */}
        {mobileTab !== 'tasks' && (
          <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
            <ChevronRight size={16} color={theme.colors.textLight} style={{ opacity: 0.5 }} />
          </div>
        )}
      </div>

      {/* Tab content — full width */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {mobileTab === 'info' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', background: theme.colors.card }}>
            <LeftPane lead={lead} statusMeta={statusMeta} onUpdate={onUpdate} showToast={showToast} isMobile />
          </div>
        )}
        {mobileTab === 'timeline' && (
          <CenterPane
            lead={lead}
            timelineKey={timelineKey}
            refreshTimeline={refreshTimeline}
            showToast={showToast}
            isMobile
          />
        )}
        {mobileTab === 'tasks' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', background: theme.colors.card }}>
            <MobileRightPane lead={lead} showToast={showToast} refreshTimeline={refreshTimeline} />
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// LEFT PANE — Contact info, stage, tags, relationships, lender
// ══════════════════════════════════════════════════════════════

function LeftPane({ lead, statusMeta, onUpdate, showToast, isMobile }) {
  const [editing, setEditing] = useState({});
  const [draft, setDraft] = useState({});
  const [relationships, setRelationships] = useState([]);
  const [showAddRel, setShowAddRel] = useState(false);

  // Load relationships
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const { data } = await supabase
          .from('contact_relationships')
          .select('id, related_id, relationship, contacts!contact_relationships_related_id_fkey(first_name, last_name)')
          .eq('contact_id', lead.id);
        setRelationships(data || []);
      } catch {}
    })();
  }, [lead.id]);

  const startEdit = (field) => {
    setEditing(e => ({ ...e, [field]: true }));
    setDraft(d => ({ ...d, [field]: lead[field] ?? '' }));
  };
  const cancelEdit = (field) => {
    setEditing(e => ({ ...e, [field]: false }));
  };
  const commit = async (field) => {
    const v = draft[field];
    if (v === (lead[field] ?? '')) { cancelEdit(field); return; }
    await onUpdate({ [field]: v === '' ? null : v });
    cancelEdit(field);
  };

  const isTerminal = ['closed', 'lost'].includes(lead.lead_status);

  return (
    <div style={{
      width: isMobile ? '100%' : 320, flexShrink: isMobile ? 1 : 0,
      borderRight: !isMobile ? `1px solid ${theme.colors.border}` : 'none',
      overflowY: 'auto', background: isMobile ? 'transparent' : `linear-gradient(180deg, ${theme.colors.card} 0%, #f8fafc 100%)`,
      padding: isMobile ? '0' : '14px 16px',
    }}>
      {/* Lead type badge */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center',
      }}>
        {lead.lead_type === 'commercial' ? (
          <>
            <span style={{
              padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
              background: '#8b5cf620', color: '#8b5cf6',
            }}>COMMERCIAL</span>
            {lead.commercial_sub_type && (
              <span style={{
                padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: (lead.commercial_sub_type === 'tenant_rep' || lead.commercial_sub_type === 'landlord_rep') ? '#8b5cf610' : '#3b82f610',
                color: (lead.commercial_sub_type === 'tenant_rep' || lead.commercial_sub_type === 'landlord_rep') ? '#8b5cf6' : '#3b82f6',
              }}>
                {lead.commercial_sub_type === 'tenant_rep' || lead.commercial_sub_type === 'landlord_rep' ? 'LEASE' : 'SALE'}
              </span>
            )}
          </>
        ) : (
          <span style={{
            padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
            background: '#22c55e20', color: '#22c55e',
          }}>RESIDENTIAL</span>
        )}
        <span style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, textTransform: 'capitalize' }}>
          {lead.lead_type || '—'}
        </span>
      </div>

      {/* Stage pipeline */}
      {!isTerminal && (
        <Section title="Lead Status">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {LEAD_STATUSES.filter(s => s.value !== 'lost').map(s => {
              const isCurrent = s.value === lead.lead_status;
              return (
                <button key={s.value} onClick={() => { if (!isCurrent) onUpdate({ lead_status: s.value }); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '5px 8px', borderRadius: 6,
                    border: isCurrent ? `2px solid ${s.color}` : `1px solid ${theme.colors.border}`,
                    background: isCurrent ? `${s.color}10` : 'transparent',
                    cursor: isCurrent ? 'default' : 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = `${s.color}08`; }}
                  onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{
                    width: 12, height: 12, borderRadius: 6, flexShrink: 0,
                    border: `2px solid ${isCurrent ? s.color : theme.colors.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isCurrent && <div style={{ width: 6, height: 6, borderRadius: 3, background: s.color }} />}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? s.color : theme.colors.text }}>
                    {s.label}
                  </span>
                </button>
              );
            })}
            <button onClick={() => onUpdate({ lead_status: 'lost' })} style={{
              marginTop: 2, padding: '5px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
              border: lead.lead_status === 'lost' ? `2px solid ${theme.colors.danger}` : `1px solid ${theme.colors.border}`,
              background: lead.lead_status === 'lost' ? `${theme.colors.danger}10` : 'transparent',
              color: theme.colors.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
            }}>
              <X size={10} /> Mark Lost
            </button>
          </div>
        </Section>
      )}

      {/* Contact info */}
      <Section title="Contact">
        <InfoField label="Email" value={lead.email} field="email" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} type="email" />
        <InfoField label="Phone" value={lead.phone} field="phone" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
        <InfoField label="Phone 2" value={lead.phone_secondary} field="phone_secondary" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
        <InfoField label="Address" value={lead.address} field="address" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
        <InfoField label="City" value={lead.city} field="city" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <InfoField label="State" value={lead.state} field="state" editing={editing} draft={draft} setDraft={setDraft}
              startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
          </div>
          <div style={{ flex: 1 }}>
            <InfoField label="Zip" value={lead.zip} field="zip" editing={editing} draft={draft} setDraft={setDraft}
              startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
          </div>
        </div>
      </Section>

      {/* Commercial-specific info */}
      {lead.lead_type === 'commercial' && (
        <Section title="Commercial Info">
          <InfoField label="Company" value={lead.company_name} field="company_name" editing={editing} draft={draft} setDraft={setDraft}
            startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
          <InfoField label="Property Type" value={lead.commercial_property_type ? lead.commercial_property_type.replace(/_/g, ' ') : null} readOnly />
          <InfoField label="Size (SF)" value={lead.desired_sf ? Number(lead.desired_sf).toLocaleString() + ' SF' : null} readOnly />
          <InfoField label="Lease/Purchase" value={lead.lease_or_purchase} readOnly />
          <InfoField label="Lease Term" value={lead.desired_lease_term} field="desired_lease_term" editing={editing} draft={draft} setDraft={setDraft}
            startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
          <InfoField label="Lease Structure" value={lead.cam_nnn_preference ? lead.cam_nnn_preference.toUpperCase() : null} readOnly />
        </Section>
      )}

      {/* Pipeline */}
      <Section title="Pipeline">
        <div style={fieldRow()}>
          <div style={fieldLabel()}>Type</div>
          <select value={lead.lead_type || ''} onChange={e => onUpdate({ lead_type: e.target.value || null })}
            style={{ ...selectStyle(), flex: 1 }}>
            <option value="">—</option>
            {LEAD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <InfoField label="Source" value={lead.lead_source} field="lead_source" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
        <InfoField label="Budget" value={lead.budget} field="budget" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
        <InfoField label="Timeline" value={lead.timeline} field="timeline" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
      </Section>

      {/* Lender */}
      <Section title="Lender">
        <InfoField label="Name" value={lead.lender_name} field="lender_name" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
        <InfoField label="Email" value={lead.lender_email} field="lender_email" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} type="email" />
        <InfoField label="Phone" value={lead.lender_phone} field="lender_phone" editing={editing} draft={draft} setDraft={setDraft}
          startEdit={startEdit} cancelEdit={cancelEdit} commit={commit} />
      </Section>

      {/* Tags */}
      <Section title="Tags">
        <TagEditor tags={lead.tags || []} onSave={(next) => onUpdate({ tags: next })} />
      </Section>

      {/* Relationships */}
      <Section title="Relationships">
        {relationships.length === 0 && !showAddRel && (
          <div style={{ fontSize: 12, color: theme.colors.textLight }}>No linked contacts</div>
        )}
        {relationships.map(r => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
            <Link2 size={11} color={theme.colors.textLight} />
            <span style={{ fontSize: 12, color: theme.colors.text }}>
              {r.contacts?.first_name} {r.contacts?.last_name}
            </span>
            <span style={{ fontSize: 10, color: theme.colors.textLight, textTransform: 'capitalize' }}>
              ({r.relationship})
            </span>
          </div>
        ))}
        <button onClick={() => setShowAddRel(!showAddRel)} style={{
          padding: '4px 8px', borderRadius: 6, border: `1px dashed ${theme.colors.border}`,
          background: 'transparent', cursor: 'pointer', fontSize: 11, color: theme.colors.textLight,
          display: 'flex', alignItems: 'center', gap: 4, marginTop: 4,
        }}>
          <Plus size={10} /> Link contact
        </button>
        {showAddRel && (
          <AddRelationship contactId={lead.id} orgId={lead.org_id} onDone={() => { setShowAddRel(false); }} showToast={showToast} />
        )}
      </Section>

      {/* Scratch notes */}
      <Section title="Quick Notes">
        <textarea
          defaultValue={lead.notes || ''}
          onBlur={(e) => { if ((e.target.value || '') !== (lead.notes || '')) onUpdate({ notes: e.target.value || null }); }}
          placeholder="Private scratchpad…"
          rows={3}
          style={{ ...inputStyle(), resize: 'vertical', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
        />
      </Section>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// CENTER PANE — Communication Timeline + Composers
// ══════════════════════════════════════════════════════════════

// ── Lead Context Card — fills empty timeline space with useful info ──
function LeadContextCard({ lead }) {
  if (!lead) return null;

  // ── Commercial context card ──
  if (lead.lead_type === 'commercial') {
    const subType = lead.commercial_sub_type;
    const isLease = subType === 'tenant_rep' || subType === 'landlord_rep';
    const subLabel = { buyer_rep: 'Buying Property', seller_rep: 'Selling Property', tenant_rep: 'Looking for Space', landlord_rep: 'Leasing Out Space' }[subType] || 'Commercial';
    const labelStyle = { fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' };
    const valueStyle = { fontSize: 13, fontWeight: 600, color: theme.colors.text };
    const smallValueStyle = { fontSize: 12, color: theme.colors.text };

    return (
      <div style={{
        borderRadius: 10, padding: '12px 14px', marginBottom: 14,
        border: '1px solid #8b5cf630',
        background: '#8b5cf606',
      }}>
        {/* Header badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <span style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
            background: '#8b5cf620', color: '#8b5cf6',
          }}>COMMERCIAL</span>
          <span style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
            background: isLease ? '#8b5cf615' : '#3b82f615',
            color: isLease ? '#8b5cf6' : '#3b82f6',
          }}>{isLease ? 'LEASE' : 'SALE'}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.textLight }}>{subLabel}</span>
        </div>

        {/* Company */}
        {lead.company_name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Briefcase size={13} color="#8b5cf6" />
            <span style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>{lead.company_name}</span>
          </div>
        )}

        {/* Space requirements */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
          {isLease ? 'Space Requirements' : 'Property Interest'}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {lead.commercial_property_type && (
            <div>
              <div style={labelStyle}>Property Type</div>
              <div style={valueStyle}>{lead.commercial_property_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
            </div>
          )}
          {lead.desired_sf && (
            <div>
              <div style={labelStyle}>{isLease ? 'Size Needed' : 'Target Size'}</div>
              <div style={valueStyle}>{Number(lead.desired_sf).toLocaleString()} SF</div>
            </div>
          )}
          {lead.budget && (
            <div>
              <div style={labelStyle}>{isLease ? 'Budget ($/SF/yr)' : 'Budget'}</div>
              <div style={valueStyle}>{lead.budget}</div>
            </div>
          )}
          {lead.desired_lease_term && (
            <div>
              <div style={labelStyle}>Lease Term</div>
              <div style={smallValueStyle}>{lead.desired_lease_term}</div>
            </div>
          )}
          {lead.cam_nnn_preference && (
            <div>
              <div style={labelStyle}>Lease Structure</div>
              <div style={smallValueStyle}>{lead.cam_nnn_preference.toUpperCase()}</div>
            </div>
          )}
          {lead.timeline && (
            <div>
              <div style={labelStyle}>Timeline</div>
              <div style={smallValueStyle}>{lead.timeline}</div>
            </div>
          )}
        </div>

        {/* LOI info if at LOI stage */}
        {['loi_submitted', 'loi_negotiation', 'loi_accepted'].includes(lead.lead_status) && lead.property_address && (
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: '#6366f110', border: '1px solid #6366f120' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>LOI Details</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>{lead.property_address}</div>
            {lead.loi_amount && <div style={{ fontSize: 12, color: theme.colors.text, marginTop: 2 }}>Amount: ${Number(lead.loi_amount).toLocaleString()}</div>}
          </div>
        )}

        {lead.notes && (
          <div style={{ marginTop: 8, fontSize: 11, color: theme.colors.textLight, lineHeight: 1.5 }}>
            {lead.notes.length > 200 ? lead.notes.slice(0, 200) + '…' : lead.notes}
          </div>
        )}
      </div>
    );
  }

  // ── Residential context card (existing) ──
  const isOfferStage = ['offer_submitted', 'offer_accepted'].includes(lead.lead_status);
  const hasOffer = lead.offer_amount || lead.property_address;
  const hasHistory = Array.isArray(lead.offer_history) && lead.offer_history.length > 0;
  const hasBudget = lead.budget || lead.timeline || lead.preapproved != null;

  // Nothing to show for very early-stage leads with no data
  if (!isOfferStage && !hasOffer && !hasBudget && !lead.notes) return null;

  const cardStyle = {
    borderRadius: 10, padding: '12px 14px', marginBottom: 14,
    border: `1px solid ${theme.colors.border}`,
    background: isOfferStage ? `linear-gradient(135deg, ${theme.colors.primary}06, ${theme.colors.success}06)` : theme.colors.card,
  };
  const labelStyle = { fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const valueStyle = { fontSize: 13, fontWeight: 600, color: theme.colors.text };
  const smallValueStyle = { fontSize: 12, color: theme.colors.text };

  // Offer/Deal context card
  if (isOfferStage || hasOffer) {
    const offerHistory = Array.isArray(lead.offer_history) ? lead.offer_history : [];
    const lastOffer = offerHistory.length > 0 ? offerHistory[offerHistory.length - 1] : null;

    return (
      <div style={cardStyle}>
        {/* Property */}
        {lead.property_address && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Home size={14} color={theme.colors.primary} />
            <span style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>{lead.property_address}</span>
          </div>
        )}

        {/* Offer summary row */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: hasHistory ? 10 : 0 }}>
          {lead.offer_amount && (
            <div>
              <div style={labelStyle}>Offer</div>
              <div style={valueStyle}>${Number(lead.offer_amount).toLocaleString()}</div>
            </div>
          )}
          {lead.accepted_price && (
            <div>
              <div style={labelStyle}>Accepted</div>
              <div style={{ ...valueStyle, color: theme.colors.success }}>${Number(lead.accepted_price).toLocaleString()}</div>
            </div>
          )}
          {lead.accepted_price && lead.offer_amount && Number(lead.accepted_price) !== Number(lead.offer_amount) && (
            <div>
              <div style={labelStyle}>Difference</div>
              <div style={{ ...valueStyle, color: Number(lead.accepted_price) > Number(lead.offer_amount) ? theme.colors.danger : theme.colors.success }}>
                {Number(lead.accepted_price) > Number(lead.offer_amount) ? '+' : ''}
                ${(Number(lead.accepted_price) - Number(lead.offer_amount)).toLocaleString()}
              </div>
            </div>
          )}
          {lead.closing_date_target && (
            <div>
              <div style={labelStyle}>Closing Target</div>
              <div style={smallValueStyle}>{formatDate(lead.closing_date_target)}</div>
            </div>
          )}
          {lead.offer_date && (
            <div>
              <div style={labelStyle}>Offer Date</div>
              <div style={smallValueStyle}>{formatDate(lead.offer_date)}</div>
            </div>
          )}
        </div>

        {/* Contingencies */}
        {Array.isArray(lead.contingencies) && lead.contingencies.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Contingencies</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {lead.contingencies.map(c => (
                <span key={c} style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                  background: `${theme.colors.primary}10`, color: theme.colors.primary,
                }}>{c.replace(/_/g, ' ')}</span>
              ))}
            </div>
          </div>
        )}

        {/* Attorneys */}
        {(lead.buyer_attorney_name || lead.seller_attorney_name) && (
          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
            {lead.buyer_attorney_name && (
              <div>
                <div style={labelStyle}>Buyer's Attorney</div>
                <div style={smallValueStyle}>{lead.buyer_attorney_name}{lead.buyer_attorney_phone ? ` · ${lead.buyer_attorney_phone}` : ''}</div>
              </div>
            )}
            {lead.seller_attorney_name && (
              <div>
                <div style={labelStyle}>Seller's Attorney</div>
                <div style={smallValueStyle}>{lead.seller_attorney_name}{lead.seller_attorney_phone ? ` · ${lead.seller_attorney_phone}` : ''}</div>
              </div>
            )}
          </div>
        )}

        {/* Negotiation summary */}
        {hasHistory && offerHistory.length > 1 && (
          <div style={{ marginTop: 8, padding: '6px 8px', borderRadius: 6, background: theme.colors.bg, fontSize: 11, color: theme.colors.textLight }}>
            {offerHistory.length} offer{offerHistory.length > 1 ? 's' : ''} in negotiation
            {lastOffer && ` · Latest: $${(lastOffer.amount || 0).toLocaleString()} (${lastOffer.type})`}
          </div>
        )}

        {/* Offer notes */}
        {lead.offer_notes && (
          <div style={{ marginTop: 6, fontSize: 11, color: theme.colors.textLight, fontStyle: 'italic' }}>
            "{lead.offer_notes}"
          </div>
        )}
      </div>
    );
  }

  // Pre-offer context: show budget, timeline, preapproval, notes
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {lead.budget && (
          <div>
            <div style={labelStyle}>Budget</div>
            <div style={valueStyle}>${typeof lead.budget === 'number' ? lead.budget.toLocaleString() : lead.budget}</div>
          </div>
        )}
        {lead.timeline && (
          <div>
            <div style={labelStyle}>Timeline</div>
            <div style={smallValueStyle}>{lead.timeline}</div>
          </div>
        )}
        {lead.preapproved != null && (
          <div>
            <div style={labelStyle}>Preapproved</div>
            <div style={smallValueStyle}>
              {lead.preapproved ? `Yes${lead.preapproval_amount ? ' · $' + Number(lead.preapproval_amount).toLocaleString() : ''}` : 'No'}
            </div>
          </div>
        )}
        {lead.lead_source && (
          <div>
            <div style={labelStyle}>Source</div>
            <div style={smallValueStyle}>{lead.lead_source}</div>
          </div>
        )}
      </div>
      {lead.notes && (
        <div style={{ marginTop: 8, fontSize: 11, color: theme.colors.textLight, lineHeight: 1.5 }}>
          {lead.notes.length > 200 ? lead.notes.slice(0, 200) + '…' : lead.notes}
        </div>
      )}
    </div>
  );
}

function CenterPane({ lead, timelineKey, refreshTimeline, showToast, isMobile }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerMode, setComposerMode] = useState('note'); // 'note' | 'sms' | 'email'
  const [callState, setCallState] = useState(null); // null | { status, callLogId }
  const [callOutcomeVisible, setCallOutcomeVisible] = useState(false);
  const [draftNote, setDraftNote] = useState('');
  const [timelineFilter, setTimelineFilter] = useState('');
  const scrollRef = useRef(null);

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/lead-timeline?contact_id=${lead.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setEvents(data.events || []);
    } catch {}
    finally { setLoading(false); }
  }, [lead.id]);

  useEffect(() => { loadTimeline(); }, [loadTimeline, timelineKey]);

  const initiateCall = async () => {
    if (!lead.phone) { showToast?.('No phone number'); return; }
    setCallState({ status: 'connecting' });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/call', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: lead.id, to_number: lead.phone }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Call failed');
      setCallState({ status: 'ringing', callLogId: data.call_log_id, callSid: data.call_sid });
      // Poll for status updates (simplified — real impl would use websockets)
      setTimeout(() => {
        setCallState(prev => prev ? { ...prev, status: 'in-progress' } : null);
      }, 5000);
    } catch (e) {
      showToast?.(e.message);
      setCallState(null);
    }
  };

  const endCall = () => {
    setCallState(null);
    setCallOutcomeVisible(true); // Show outcome chips after call ends
    refreshTimeline();
  };

  // Also show outcome chips when agent clicks Call (for click-to-call logging)
  const handleCallClick = () => {
    if (!lead.phone) { showToast?.('No phone number'); return; }
    // Open native dialer
    window.open(`tel:${lead.phone}`, '_self');
    // Show outcome chips so agent can log the result
    setCallOutcomeVisible(true);
  };

  const logCallOutcome = async (outcome) => {
    setCallOutcomeVisible(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      // Log as a timeline event + increment contact_attempts
      await fetch(`/api/lead-timeline?contact_id=${lead.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'call_outcome',
          call_outcome: outcome,
          notes: `Call outcome: ${outcome.replace(/_/g, ' ')}`,
        }),
      });
      // Increment contact attempts
      await fetch(`/api/leads?id=${lead.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_attempts: (lead.contact_attempts || 0) + 1,
          last_contacted_at: new Date().toISOString(),
        }),
      });
      showToast?.(`Logged: ${outcome.replace(/_/g, ' ')}`);
      refreshTimeline();
    } catch (e) {
      showToast?.(`Failed to log outcome: ${e.message}`);
    }
  };

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      background: theme.colors.bg, minWidth: 0, maxWidth: isMobile ? '100%' : 480,
      width: isMobile ? '100%' : 'auto',
    }}>
      {/* Communication action bar */}
      <div style={{
        display: 'flex', gap: isMobile ? 4 : 6, padding: isMobile ? '8px 12px' : '10px 16px',
        borderBottom: `1px solid ${theme.colors.border}`,
        background: theme.colors.card, overflowX: isMobile ? 'auto' : 'visible',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
      }}>
        <ActionButton
          icon={<Phone size={14} />}
          label="Call"
          color="#16A34A"
          disabled={!lead.phone || !!callState}
          onClick={handleCallClick}
        />
        <ActionButton
          icon={<MessageSquare size={14} />}
          label="Text"
          color="#3B82F6"
          disabled={!lead.phone}
          active={composerMode === 'sms'}
          onClick={() => setComposerMode(composerMode === 'sms' ? 'note' : 'sms')}
        />
        <ActionButton
          icon={<Mail size={14} />}
          label="Email"
          color="#8B5CF6"
          disabled={!lead.email}
          onClick={() => { if (lead.email) window.open(`mailto:${lead.email}`, '_blank'); }}
        />
        <ActionButton
          icon={<FileText size={14} />}
          label="Note"
          color={theme.colors.primary}
          active={composerMode === 'note'}
          onClick={() => setComposerMode('note')}
        />
        {/* Residential: Showing | Commercial: Tour + LOI */}
        {lead.lead_type === 'commercial' ? (
          <>
            <ActionButton
              icon={<Home size={14} />}
              label="Tour"
              color="#06B6D4"
              onClick={() => { setComposerMode('note'); setDraftNote('🏢 Scheduled property tour: '); }}
            />
            <ActionButton
              icon={<FileText size={14} />}
              label="LOI"
              color="#6366F1"
              onClick={() => { setComposerMode('note'); setDraftNote('📄 LOI submitted: '); }}
            />
          </>
        ) : (
          <ActionButton
            icon={<Home size={14} />}
            label="Showing"
            color="#06B6D4"
            onClick={() => { setComposerMode('note'); setDraftNote('🏠 Scheduled showing: '); }}
          />
        )}
      </div>

      {/* Call outcome quick-log — appears after a call action */}
      {callOutcomeVisible && (
        <div style={{
          display: 'flex', gap: 6, padding: '8px 16px', flexWrap: 'wrap',
          background: lead.lead_type === 'commercial' ? '#8b5cf608' : '#22c55e08',
          borderBottom: `1px solid ${theme.colors.border}`,
          alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: lead.lead_type === 'commercial' ? '#8b5cf6' : '#22c55e', marginRight: 4 }}>
            Call outcome:
          </span>
          {(lead.lead_type === 'commercial'
            ? [
                { key: 'connected', label: 'Connected' },
                { key: 'voicemail', label: 'Voicemail' },
                { key: 'no_answer', label: 'No answer' },
                { key: 'sent_spaces', label: 'Sent spaces' },
                { key: 'scheduled_tour', label: 'Sched. tour' },
              ]
            : [
                { key: 'connected', label: 'Connected' },
                { key: 'voicemail', label: 'Voicemail' },
                { key: 'no_answer', label: 'No answer' },
                { key: 'wrong_number', label: 'Wrong number' },
                { key: 'scheduled_showing', label: 'Sched. showing' },
              ]
          ).map(o => (
            <button key={o.key} onClick={() => logCallOutcome(o.key)} style={{
              padding: '4px 12px', borderRadius: 14, fontSize: 11, fontWeight: 600,
              border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
              color: theme.colors.text, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = lead.lead_type === 'commercial' ? '#8b5cf615' : '#22c55e15'; }}
            onMouseLeave={e => { e.currentTarget.style.background = theme.colors.card; }}
            >
              {o.label}
            </button>
          ))}
          <button onClick={() => setCallOutcomeVisible(false)} style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
            color: theme.colors.textLight, padding: 2,
          }}>✕</button>
        </div>
      )}

      {/* Active call bar */}
      {callState && (
        <CallBar callState={callState} lead={lead} onEnd={endCall} showToast={showToast} />
      )}

      {/* ═══ Scrollable content area ═══ */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>

        {/* ── Last Activity Summary ── */}
        {events.length > 0 && (() => {
          const lastEvent = events[0]; // events are sorted newest-first
          const lastMeta = timelineIconFor(lastEvent.kind);
          const daysSince = Math.floor((Date.now() - new Date(lastEvent.at).getTime()) / 86400000);
          const daysLabel = daysSince === 0 ? 'Today' : daysSince === 1 ? 'Yesterday' : `${daysSince} days ago`;
          return (
            <div style={{
              padding: '10px 14px', borderRadius: 10, marginBottom: 12,
              background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Last Activity
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: lastMeta.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: lastMeta.color }}>{lastMeta.label} {daysLabel}</span>
                <span style={{ fontSize: 11, color: theme.colors.textLight, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  — {(lastEvent.body || '').slice(0, 80)}{(lastEvent.body || '').length > 80 ? '…' : ''}
                </span>
              </div>
              {lead.next_action && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11 }}>
                  <span style={{ color: theme.colors.textLight }}>Next:</span>
                  <span style={{ color: theme.colors.text, fontWeight: 500 }}>{lead.next_action}</span>
                  {lead.next_action_date && (
                    <span style={{ marginLeft: 'auto', fontWeight: 600, color: new Date(lead.next_action_date) < new Date() ? '#EF4444' : '#F59E0B' }}>
                      {new Date(lead.next_action_date) < new Date() ? 'Overdue' : `Due ${formatDateRelative(lead.next_action_date)}`}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Context Card (residential or commercial) ── */}
        <LeadContextCard lead={lead} />

        {/* ── Staleness nudge (cool/cold leads not in terminal state) ── */}
        {(() => {
          const s = stalenessOf(lead);
          const ls = (lead.lead_status || '').toLowerCase();
          if (['closed', 'lost'].includes(ls)) return null;
          if (s === 'cold') {
            const d = daysSinceContact(lead);
            return (
              <div style={{
                padding: '8px 12px', borderRadius: 8, marginBottom: 12,
                background: '#ef444410', border: '1px solid #ef444425',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertCircle size={14} color="#ef4444" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                  No contact in {d ?? '14+'}  days — this lead is going cold. Send a text or make a call to re-engage.
                </span>
              </div>
            );
          }
          if (s === 'cool') {
            const d = daysSinceContact(lead);
            return (
              <div style={{
                padding: '8px 12px', borderRadius: 8, marginBottom: 12,
                background: '#eab30810', border: '1px solid #eab30825',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Clock size={14} color="#eab308" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>
                  Last contacted {d} days ago — follow up soon to keep this lead warm.
                </span>
              </div>
            );
          }
          return null;
        })()}

        {/* ── Timeline header with filter ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, marginTop: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Timeline
          </span>
          <select
            value={timelineFilter || 'all'}
            onChange={e => setTimelineFilter(e.target.value === 'all' ? '' : e.target.value)}
            style={{
              fontSize: 11, fontWeight: 600, color: theme.colors.primary, background: 'transparent',
              border: 'none', cursor: 'pointer', outline: 'none',
            }}
          >
            <option value="all">All activity</option>
            <option value="call">Calls</option>
            <option value="message">Texts</option>
            <option value="email_sent">Emails</option>
            <option value="note">Notes</option>
            <option value="status">Status changes</option>
          </select>
        </div>

        {/* ── Timeline entries (card-based) ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: theme.colors.textLight, fontSize: 13 }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading timeline…
          </div>
        ) : events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.colors.textLight }}>
            <MessageSquare size={32} color={theme.colors.border} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>No activity yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Send a text, make a call, or add a note to get started.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {events
              .filter(e => !timelineFilter || e.kind === timelineFilter)
              .map((e, i) => <TimelineEventCard key={`${e.ref_id || i}-${e.at}`} event={e} />)
            }
          </div>
        )}
      </div>

      {/* Inline composer */}
      <div style={{
        borderTop: `1px solid ${theme.colors.border}`,
        background: theme.colors.card, flexShrink: 0,
      }}>
        {composerMode === 'sms' ? (
          <SmsComposer contactId={lead.id} phone={lead.phone} onSent={() => { refreshTimeline(); }} showToast={showToast} />
        ) : (
          <NoteComposer contactId={lead.id} onPosted={() => { refreshTimeline(); }} showToast={showToast} />
        )}
      </div>
    </div>
  );
}

// ── Call Bar (shown during active call) ──

function CallBar({ callState, lead, onEnd, showToast }) {
  const [elapsed, setElapsed] = useState(0);
  const [disposition, setDisposition] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;

  const saveDisposition = async () => {
    if (callState.callLogId && disposition) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await fetch(`/api/call?id=${callState.callLogId}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ disposition }),
        });
      } catch {}
    }
    onEnd();
  };

  return (
    <div style={{
      padding: '10px 16px', background: '#ECFDF5', borderBottom: `1px solid ${theme.colors.border}`,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 16, background: '#16A34A',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: callState.status === 'connecting' || callState.status === 'ringing' ? 'pulse 1.5s infinite' : 'none',
      }}>
        <PhoneCall size={16} color="white" />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#16A34A' }}>
          {callState.status === 'connecting' ? 'Connecting…' :
           callState.status === 'ringing' ? 'Ringing your phone…' :
           `In call with ${lead.first_name}`}
        </div>
        <div style={{ fontSize: 11, color: theme.colors.textLight }}>
          {minutes}:{seconds.toString().padStart(2, '0')}
        </div>
      </div>
      <select value={disposition} onChange={e => setDisposition(e.target.value)}
        style={{ ...selectStyle(), fontSize: 11, width: 150 }}>
        <option value="">Disposition…</option>
        {DISPOSITIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
      </select>
      <button onClick={saveDisposition} style={{
        padding: '6px 12px', borderRadius: 8, border: 'none',
        background: theme.colors.danger, color: 'white',
        fontSize: 11, fontWeight: 700, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <PhoneOff size={12} /> End
      </button>
    </div>
  );
}

// ── SMS Composer ──

function SmsComposer({ contactId, phone, onSent, showToast }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/contact-sms', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contactId, body: text.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to send');
      setText('');
      onSent();
      showToast?.('SMS sent');
    } catch (e) { showToast?.(e.message); }
    finally { setSending(false); }
  };

  return (
    <div style={{ padding: '10px 16px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#3B82F6', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
        <MessageSquare size={10} style={{ verticalAlign: '-1px' }} /> Text Message → {phone}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) send(); }}
          placeholder="Type your message… ⌘+Enter to send"
          rows={2}
          style={{ ...inputStyle(), flex: 1, resize: 'none', fontFamily: 'inherit' }}
        />
        <button onClick={send} disabled={sending || !text.trim()} style={{
          ...btnPrimary(), background: '#3B82F6', alignSelf: 'flex-end',
          opacity: (sending || !text.trim()) ? 0.5 : 1,
        }}>
          {sending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
        </button>
      </div>
      <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 4 }}>
        {text.length}/160 · {Math.ceil(text.length / 160) || 1} segment{text.length > 160 ? 's' : ''}
      </div>
    </div>
  );
}

// ── Note Composer (with @mentions) ──

function NoteComposer({ contactId, onPosted, showToast }) {
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [posting, setPosting] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: me } = await supabase.from('users').select('org_id').eq('id', user.id).maybeSingle();
        if (!me?.org_id) return;
        const { data: users } = await supabase.from('users')
          .select('id, full_name').eq('org_id', me.org_id).order('full_name');
        setTeammates(users || []);
      } catch {}
    })();
  }, []);

  const handleTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    const m = before.match(/@(\w*)$/);
    if (m) setMentionQuery({ start: caret - m[0].length, query: m[1].toLowerCase() });
    else setMentionQuery(null);
  };

  const filteredTeammates = mentionQuery
    ? teammates.filter(t => (t.full_name || '').toLowerCase().includes(mentionQuery.query))
    : [];

  const pickMention = (user) => {
    if (!mentionQuery) return;
    const before = text.slice(0, mentionQuery.start);
    const after = text.slice(mentionQuery.start + 1 + mentionQuery.query.length);
    setText(before + `@${user.full_name} ` + after);
    setMentions(ms => (ms.includes(user.id) ? ms : [...ms, user.id]));
    setMentionQuery(null);
  };

  const postNote = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/contact-notes', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contactId, body: text.trim(), mentions }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      setText(''); setMentions([]);
      onPosted();
    } catch (e) { showToast?.(e.message); }
    finally { setPosting(false); }
  };

  return (
    <div style={{ padding: '10px 16px', position: 'relative' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.primary, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
        <FileText size={10} style={{ verticalAlign: '-1px' }} /> Add Note
        {mentions.length > 0 && <span style={{ color: theme.colors.textLight, fontWeight: 500 }}> · {mentions.length} mention{mentions.length > 1 ? 's' : ''}</span>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <textarea
            value={text}
            onChange={handleTextChange}
            onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) postNote(); }}
            placeholder="Add a note… use @ to mention a teammate. ⌘+Enter to post."
            rows={2}
            style={{ ...inputStyle(), width: '100%', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
          {mentionQuery && filteredTeammates.length > 0 && (
            <div style={{
              position: 'absolute', left: 0, bottom: '100%', marginBottom: 4,
              background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
              borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              maxHeight: 160, overflowY: 'auto', zIndex: 20, minWidth: 200,
            }}>
              {filteredTeammates.slice(0, 5).map(t => (
                <button key={t.id} onClick={() => pickMention(t)} style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '6px 10px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 12, color: theme.colors.text,
                }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  @{t.full_name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={postNote} disabled={posting || !text.trim()} style={{
          ...btnPrimary(), alignSelf: 'flex-end',
          opacity: (posting || !text.trim()) ? 0.5 : 1,
        }}>
          {posting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
        </button>
      </div>
    </div>
  );
}

// ── Timeline Event Renderer ──

function TimelineEvent({ event }) {
  const meta = timelineIconFor(event.kind);
  const Icon = meta.icon;
  const when = formatDateTime(event.at);

  const isMessage = event.kind === 'message';
  const isOutbound = event.meta?.direction === 'outbound';
  const isCall = event.kind === 'call';
  const isEmail = event.kind === 'email_sent';

  return (
    <div style={{
      display: 'flex', gap: 10, padding: '8px 0',
      borderBottom: `1px solid ${theme.colors.border}08`,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 14, flexShrink: 0,
        background: `${meta.color}15`, color: meta.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={13} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {meta.label}
          </span>
          {isMessage && (
            <span style={{
              fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 4,
              background: isOutbound ? '#3B82F615' : '#10B98115',
              color: isOutbound ? '#3B82F6' : '#10B981',
            }}>
              {isOutbound ? 'SENT' : 'RECEIVED'}
            </span>
          )}
          {isCall && event.meta?.recording_url && (
            <RecordingPlayer url={event.meta.recording_url} duration={event.meta.recording_duration_secs} />
          )}
          <span style={{ fontSize: 10, color: theme.colors.textLight, marginLeft: 'auto' }}>{when}</span>
        </div>
        <div style={{ fontSize: 13, color: theme.colors.text, marginTop: 3, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
          {highlightMentions(event.body || '')}
        </div>
        {isEmail && event.meta?.opened_at && (
          <div style={{ fontSize: 10, color: theme.colors.success, marginTop: 2, fontWeight: 600 }}>
            ✓ Opened {formatDate(event.meta.opened_at)}
            {event.meta.clicked_at && ` · Clicked ${formatDate(event.meta.clicked_at)}`}
          </div>
        )}
        {isCall && event.meta?.disposition && (
          <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2, textTransform: 'capitalize' }}>
            {event.meta.disposition.replace(/_/g, ' ')}
            {event.meta.call_notes && ` — ${event.meta.call_notes}`}
          </div>
        )}
        {(event.author_name || event.agent_name) && (
          <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>
            by {event.author_name || event.agent_name}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Recording Player ──

// ── Card-based Timeline Event (redesigned) ──
function TimelineEventCard({ event }) {
  const meta = timelineIconFor(event.kind);
  const Icon = meta.icon;
  const when = formatDateTime(event.at);

  const isMessage = event.kind === 'message';
  const isOutbound = event.meta?.direction === 'outbound';
  const isCall = event.kind === 'call';
  const isEmail = event.kind === 'email_sent';

  return (
    <div style={{
      padding: '10px 14px', borderRadius: 10,
      background: theme.colors.card,
      border: `1px solid ${theme.colors.border}`,
    }}>
      {/* Header: icon dot + type label + time */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <div style={{ width: 8, height: 8, borderRadius: 4, background: meta.color, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>
          {meta.label}
        </span>
        {isMessage && (
          <span style={{
            fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
            background: isOutbound ? '#3B82F612' : '#10B98112',
            color: isOutbound ? '#3B82F6' : '#10B981',
          }}>
            {isOutbound ? 'SENT' : 'RECEIVED'}
          </span>
        )}
        {isCall && event.meta?.disposition && (
          <span style={{
            fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
            background: event.meta.disposition === 'connected' ? '#22c55e12' : '#64748b12',
            color: event.meta.disposition === 'connected' ? '#22c55e' : '#64748b',
            textTransform: 'capitalize',
          }}>
            {event.meta.disposition.replace(/_/g, ' ')}
          </span>
        )}
        <span style={{ fontSize: 10, color: theme.colors.textLight, marginLeft: 'auto', flexShrink: 0 }}>{when}</span>
      </div>

      {/* Body */}
      <div style={{ fontSize: 12, color: theme.colors.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
        {highlightMentions(event.body || '')}
      </div>

      {/* Email tracking */}
      {isEmail && event.meta?.opened_at && (
        <div style={{ fontSize: 10, color: theme.colors.success, marginTop: 4, fontWeight: 600 }}>
          Opened ✓ {event.meta.clicked_at && `· Clicked ${event.meta.click_count || ''} listing${event.meta.click_count > 1 ? 's' : ''}`}
        </div>
      )}

      {/* Call details */}
      {isCall && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          {event.meta?.call_duration_secs && (
            <span style={{ fontSize: 10, color: theme.colors.textLight, fontStyle: 'italic' }}>
              Duration: {Math.floor(event.meta.call_duration_secs / 60)} min {event.meta.call_duration_secs % 60}s
            </span>
          )}
          {event.meta?.recording_url && (
            <RecordingPlayer url={event.meta.recording_url} duration={event.meta.recording_duration_secs} />
          )}
        </div>
      )}

      {/* Call notes */}
      {isCall && event.meta?.call_notes && (
        <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 4, fontStyle: 'italic' }}>
          {event.meta.call_notes}
        </div>
      )}

      {/* Author */}
      {(event.author_name || event.agent_name) && (
        <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 4 }}>
          Agent: {event.author_name || event.agent_name}
        </div>
      )}
    </div>
  );
}

function RecordingPlayer({ url, duration }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const durLabel = duration ? `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}` : '';

  return (
    <button onClick={toggle} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '1px 6px', borderRadius: 4, border: `1px solid ${theme.colors.border}`,
      background: playing ? `${theme.colors.primary}10` : 'transparent',
      cursor: 'pointer', fontSize: 9, fontWeight: 600, color: theme.colors.primary,
    }}>
      {playing ? <Pause size={9} /> : <Play size={9} />}
      {durLabel && <span>{durLabel}</span>}
      <Volume2 size={9} />
    </button>
  );
}

// ══════════════════════════════════════════════════════════════
// MOBILE RIGHT PANE — Stacked tasks, deals, plans (no tabs)
// ══════════════════════════════════════════════════════════════

function MobileRightPane({ lead, showToast, refreshTimeline }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Tasks section */}
      <div>
        <div style={{
          fontSize: 12, fontWeight: 700, color: theme.colors.textLight,
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
        }}>Tasks</div>
        <TasksPanel lead={lead} showToast={showToast} />
      </div>

      {/* Deals section */}
      <div>
        <div style={{
          fontSize: 12, fontWeight: 700, color: theme.colors.textLight,
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
        }}>Deals</div>
        <DealsPanel lead={lead} />
      </div>

      {/* Plans section */}
      <div>
        <div style={{
          fontSize: 12, fontWeight: 700, color: theme.colors.textLight,
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
        }}>Action Plans</div>
        <PlansPanel lead={lead} showToast={showToast} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// RIGHT PANE — Tasks, Deals, Action Plans
// ══════════════════════════════════════════════════════════════

function RightPane({ lead, showToast, refreshTimeline, updateLead }) {
  const [activeTab, setActiveTab] = useState('tasks');

  const hasOffers = lead?.lead_status === 'offer_submitted' || lead?.lead_status === 'offer_accepted'
    || (Array.isArray(lead?.offer_history) && lead.offer_history.length > 0)
    || lead?.offer_amount;

  const tabs = [
    { key: 'tasks', label: 'Tasks', icon: CheckCircle2, color: '#EF4444' },
    ...(hasOffers ? [{ key: 'offers', label: 'Offers', icon: DollarSign, color: '#F59E0B' }] : []),
    { key: 'deals', label: 'Deals', icon: Briefcase, color: '#16A34A' },
    { key: 'plans', label: 'Plans', icon: Calendar, color: '#8B5CF6' },
    { key: 'web', label: 'Web', icon: Globe, color: '#3B82F6' },
  ];

  return (
    <div style={{
      width: 320, flexShrink: 0, borderLeft: `1px solid ${theme.colors.border}`,
      display: 'flex', flexDirection: 'column', background: `linear-gradient(180deg, ${theme.colors.card} 0%, #f8fafc 100%)`,
    }}>
      {/* Tab strip */}
      <div style={{
        display: 'flex', borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0,
        background: theme.colors.card,
      }}>
        {tabs.map(t => {
          const active = activeTab === t.key;
          const TabIcon = t.icon;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              flex: 1, padding: '10px 4px', border: 'none',
              borderBottom: active ? `2.5px solid ${t.color}` : '2.5px solid transparent',
              background: active ? `${t.color}06` : 'transparent',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              fontSize: 11, fontWeight: active ? 700 : 500,
              color: active ? t.color : theme.colors.textLight,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.background = `${t.color}04`; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
            >
              <TabIcon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
        {activeTab === 'tasks' && <TasksPanel lead={lead} showToast={showToast} />}
        {activeTab === 'offers' && <OffersPanel lead={lead} showToast={showToast} onUpdate={updateLead} />}
        {activeTab === 'deals' && <DealsPanel lead={lead} />}
        {activeTab === 'plans' && <PlansPanel lead={lead} showToast={showToast} />}
        {activeTab === 'web' && <WebActivityPanel contactId={lead.id} />}
      </div>
    </div>
  );
}

// ── Tasks Panel ──

function TasksPanel({ lead, showToast }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [dueDate, setDueDate] = useState(() => new Date(Date.now() + 86400000).toISOString().split('T')[0]);
  // ── Agent assignment ──
  const [agents, setAgents] = useState([]);
  const [assignee, setAssignee] = useState(null); // { id, name } or null = self
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const assigneeRef = useRef(null);

  // Load agents + detect role on mount
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const currentUserId = session.user?.id;
        // Try /api/team — only succeeds for broker/admin
        const r = await fetch('/api/team', { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (r.ok) {
          const data = await r.json();
          const members = data.team || data.members || [];
          setAgents(members.map(u => ({
            id: u.id, name: u.full_name || u.name || u.email, email: u.email,
          })));
          // Find current user's role from the team list
          const me = members.find(m => m.id === currentUserId);
          if (me?.role) setUserRole(me.role);
          else setUserRole('broker'); // if team endpoint succeeded, caller is broker/admin
        }
        // If 403 → agent role, no dropdown needed
      } catch { /* noop */ }
    })();
  }, []);

  // Close assignee dropdown on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) setShowAssigneeMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isBroker = userRole === 'broker' || userRole === 'admin';
  const filteredAgents = agents.filter(a => {
    if (!assigneeQuery) return true;
    const q = assigneeQuery.toLowerCase();
    return (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q);
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/tasks?range=all', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setTasks((data.tasks || []).filter(t => t.lead_id === lead.id));
    } catch {} finally { setLoading(false); }
  }, [lead.id]);

  useEffect(() => { load(); }, [load]);

  const addTask = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/tasks', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle.trim(),
          due_date: dueDate,
          lead_id: lead.id,
          agent_id: assignee?.id || lead.assigned_to,
        }),
      });
      if (!r.ok) throw new Error('Failed');
      const assigneeName = assignee?.name;
      setNewTitle('');
      setDueDate(new Date(Date.now() + 86400000).toISOString().split('T')[0]); // reset to tomorrow
      setAssignee(null);
      setAssigneeQuery('');
      load();
      if (assigneeName) showToast?.(`Task assigned to ${assigneeName}`);
    } catch (e) { showToast?.(e.message); }
    finally { setAdding(false); }
  };

  const toggleTask = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`/api/tasks?id=${task.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, completed_source: 'web' }),
      });
      load();
    } catch {}
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addTask(); }}
          placeholder="Add task…"
          style={{ ...inputStyle(), flex: 1, fontSize: 11, padding: '6px 8px' }}
        />
        <button onClick={addTask} disabled={adding || !newTitle.trim()} style={{
          ...btnPrimary(), padding: '6px 8px', fontSize: 11,
          opacity: (adding || !newTitle.trim()) ? 0.5 : 1,
        }}>
          <Plus size={12} />
        </button>
      </div>
      {/* Due date + quick picks */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: isBroker ? 4 : 10 }}>
        <Calendar size={11} color={theme.colors.textLight} />
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
          style={{
            border: `1px solid ${theme.colors.border}`, borderRadius: 6,
            padding: '3px 6px', fontSize: 11, color: theme.colors.text,
            background: theme.colors.card, cursor: 'pointer',
          }}
        />
        {[
          { label: 'Today', days: 0 },
          { label: 'Tmrw', days: 1 },
          { label: '+1w', days: 7 },
        ].map(opt => {
          const d = new Date(Date.now() + opt.days * 86400000).toISOString().split('T')[0];
          const active = dueDate === d;
          return (
            <button key={opt.label} onClick={() => setDueDate(d)} style={{
              padding: '2px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600,
              border: `1px solid ${active ? theme.colors.primary : theme.colors.border}`,
              background: active ? `${theme.colors.primary}15` : 'transparent',
              color: active ? theme.colors.primary : theme.colors.textLight,
              cursor: 'pointer',
            }}>
              {opt.label}
            </button>
          );
        })}
      </div>
      {/* Agent assignment row — broker/admin only */}
      {isBroker && agents.length > 0 && (
        <div ref={assigneeRef} style={{ position: 'relative', marginBottom: 10 }}>
          <div
            onClick={() => setShowAssigneeMenu(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px',
              borderRadius: 8, border: `1px solid ${theme.colors.border}`,
              background: theme.colors.card, cursor: 'pointer', fontSize: 11,
              color: assignee ? theme.colors.text : theme.colors.textLight,
            }}
          >
            <Users size={11} />
            <span style={{ flex: 1 }}>{assignee ? assignee.name : 'Assign to… (default: lead owner)'}</span>
            <ChevronDown size={11} />
          </div>
          {showAssigneeMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
              borderRadius: 8, marginTop: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              maxHeight: 180, overflowY: 'auto',
            }}>
              <input
                value={assigneeQuery} onChange={e => setAssigneeQuery(e.target.value)}
                placeholder="Search agent…" autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '6px 8px',
                  border: 'none', borderBottom: `1px solid ${theme.colors.border}`,
                  fontSize: 11, outline: 'none', background: 'transparent',
                  color: theme.colors.text,
                }}
              />
              {/* Clear option */}
              {assignee && (
                <div
                  onClick={() => { setAssignee(null); setAssigneeQuery(''); setShowAssigneeMenu(false); }}
                  style={{
                    padding: '6px 8px', cursor: 'pointer', fontSize: 11,
                    color: theme.colors.danger, borderBottom: `1px solid ${theme.colors.border}`,
                  }}
                >
                  ✕ Clear (use lead owner)
                </div>
              )}
              {filteredAgents.map(a => (
                <div key={a.id}
                  onClick={() => { setAssignee(a); setAssigneeQuery(''); setShowAssigneeMenu(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
                    cursor: 'pointer', fontSize: 11,
                    background: assignee?.id === a.id ? `${theme.colors.primary}10` : 'transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${theme.colors.primary}10`}
                  onMouseLeave={e => e.currentTarget.style.background = assignee?.id === a.id ? `${theme.colors.primary}10` : 'transparent'}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: 10, flexShrink: 0,
                    background: theme.colors.primary, color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 8, fontWeight: 700,
                  }}>
                    {(a.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: theme.colors.text }}>{a.name}</div>
                    {a.email && <div style={{ color: theme.colors.textLight, fontSize: 10 }}>{a.email}</div>}
                  </div>
                </div>
              ))}
              {filteredAgents.length === 0 && (
                <div style={{ padding: '8px', fontSize: 11, color: theme.colors.textLight, textAlign: 'center' }}>No match</div>
              )}
            </div>
          )}
        </div>
      )}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 16, color: theme.colors.textLight, fontSize: 12 }}>
          <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 16, color: theme.colors.textLight, fontSize: 12 }}>No tasks</div>
      ) : (
        tasks.map(t => {
          const done = t.status === 'completed';
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0',
              borderBottom: `1px solid ${theme.colors.border}`, opacity: done ? 0.5 : 1,
            }}>
              <button onClick={() => toggleTask(t)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                color: done ? theme.colors.success : theme.colors.textLight,
              }}>
                {done ? <CheckCircle2 size={15} /> : <span style={{
                  width: 15, height: 15, borderRadius: 8, border: `1.5px solid ${theme.colors.border}`,
                  display: 'inline-block',
                }} />}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, textDecoration: done ? 'line-through' : 'none' }}>
                  {t.title}
                </div>
                {t.due_date && <div style={{ fontSize: 10, color: theme.colors.textLight }}>Due {formatDateRelative(t.due_date)}</div>}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Offers Panel — revision history + add counter/revised offers ──

function OffersPanel({ lead, showToast, onUpdate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newOffer, setNewOffer] = useState({ type: 'counter', amount: '', from: 'seller', date: new Date().toISOString().split('T')[0], notes: '' });
  const [saving, setSaving] = useState(false);

  const history = Array.isArray(lead?.offer_history) ? lead.offer_history : [];

  const TYPE_META = {
    initial:  { label: 'Initial Offer',   color: '#6366F1', icon: '📝' },
    counter:  { label: 'Counter Offer',    color: '#F59E0B', icon: '↩️' },
    revised:  { label: 'Revised Offer',    color: '#0EA5E9', icon: '✏️' },
    accepted: { label: 'Accepted',         color: '#16A34A', icon: '✅' },
    rejected: { label: 'Rejected',         color: '#EF4444', icon: '❌' },
  };

  const formatCurrency = (raw) => {
    const num = String(raw).replace(/[^0-9]/g, '');
    return num ? Number(num).toLocaleString('en-US') : '';
  };

  const addOffer = async () => {
    const amountNum = parseFloat(String(newOffer.amount).replace(/[^\d.]/g, ''));
    if (!amountNum) { showToast?.('Enter an offer amount'); return; }
    setSaving(true);
    try {
      const entry = {
        type: newOffer.type,
        amount: amountNum,
        from: newOffer.from,
        date: newOffer.date,
        notes: newOffer.notes,
        created_at: new Date().toISOString(),
      };
      const updatedHistory = [...history, entry];
      await onUpdate({ offer_history: updatedHistory });
      setShowAdd(false);
      setNewOffer({ type: 'counter', amount: '', from: 'seller', date: new Date().toISOString().split('T')[0], notes: '' });
      showToast?.(`${TYPE_META[entry.type]?.label || 'Offer'} added`);
    } catch (e) { showToast?.(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {/* Summary bar */}
      {lead?.property_address && (
        <div style={{ padding: '8px 10px', background: `${theme.colors.primary}08`, borderRadius: 8, marginBottom: 10, fontSize: 12, color: theme.colors.text }}>
          <div style={{ fontWeight: 700 }}>{lead.property_address}</div>
          <div style={{ color: theme.colors.textLight, marginTop: 2 }}>
            {lead.offer_amount && `Offer: $${Number(lead.offer_amount).toLocaleString()}`}
            {lead.accepted_price && ` → Accepted: $${Number(lead.accepted_price).toLocaleString()}`}
            {lead.closing_date_target && ` · Closing: ${formatDate(lead.closing_date_target)}`}
          </div>
        </div>
      )}

      {/* Add offer button */}
      {lead?.lead_status !== 'offer_accepted' && (
        <button onClick={() => setShowAdd(v => !v)} style={{
          display: 'flex', alignItems: 'center', gap: 5, width: '100%',
          padding: '7px 10px', marginBottom: 10, borderRadius: 8,
          border: `1px dashed ${theme.colors.border}`, background: 'transparent',
          cursor: 'pointer', fontSize: 11, fontWeight: 600, color: theme.colors.primary,
        }}>
          <Plus size={12} /> Add Counter / Revised Offer
        </button>
      )}

      {/* Add offer form */}
      {showAdd && (
        <div style={{
          padding: 10, borderRadius: 8, marginBottom: 10,
          background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <select value={newOffer.type} onChange={e => setNewOffer(v => ({ ...v, type: e.target.value }))}
              style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 11, background: theme.colors.card, color: theme.colors.text }}>
              <option value="counter">Counter Offer</option>
              <option value="revised">Revised Offer</option>
              <option value="rejected">Rejected</option>
            </select>
            <select value={newOffer.from} onChange={e => setNewOffer(v => ({ ...v, from: e.target.value }))}
              style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 11, background: theme.colors.card, color: theme.colors.text }}>
              <option value="buyer">From Buyer</option>
              <option value="seller">From Seller</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: theme.colors.textLight }}>$</span>
              <input value={formatCurrency(newOffer.amount)}
                onChange={e => setNewOffer(v => ({ ...v, amount: formatCurrency(e.target.value) }))}
                placeholder="460,000" inputMode="numeric"
                style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px 6px 20px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 11, background: theme.colors.card, color: theme.colors.text }}
              />
            </div>
            <input type="date" value={newOffer.date}
              onChange={e => setNewOffer(v => ({ ...v, date: e.target.value }))}
              style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 11, background: theme.colors.card, color: theme.colors.text }}
            />
          </div>
          <input value={newOffer.notes} onChange={e => setNewOffer(v => ({ ...v, notes: e.target.value }))}
            placeholder="Notes (e.g. seller countered at $470K, buyer revised to $465K)"
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 11, marginBottom: 8, background: theme.colors.card, color: theme.colors.text }}
          />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAdd(false)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, background: 'transparent', fontSize: 11, cursor: 'pointer', color: theme.colors.text }}>Cancel</button>
            <button onClick={addOffer} disabled={saving} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: theme.colors.primary, color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Offer history timeline */}
      {history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: theme.colors.textLight, fontSize: 12 }}>
          No offers yet. Move the lead to "Offer Submitted" to start tracking.
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 18 }}>
          {/* Timeline line */}
          <div style={{ position: 'absolute', left: 7, top: 4, bottom: 4, width: 2, background: theme.colors.border }} />

          {history.map((entry, i) => {
            const meta = TYPE_META[entry.type] || TYPE_META.initial;
            const isLast = i === history.length - 1;
            return (
              <div key={i} style={{ position: 'relative', marginBottom: i < history.length - 1 ? 14 : 0 }}>
                {/* Timeline dot */}
                <div style={{
                  position: 'absolute', left: -14, top: 2,
                  width: 14, height: 14, borderRadius: 7,
                  background: meta.color, border: '2px solid white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }} />

                <div style={{
                  padding: '8px 10px', borderRadius: 8,
                  background: isLast ? `${meta.color}10` : theme.colors.bg,
                  border: isLast ? `1px solid ${meta.color}30` : `1px solid ${theme.colors.border}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>
                      {meta.icon} {meta.label}
                    </span>
                    <span style={{ fontSize: 10, color: theme.colors.textLight }}>
                      {formatDate(entry.date)}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>
                    ${(entry.amount || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>
                    From {entry.from || 'buyer'}
                    {entry.contingencies?.length > 0 && ` · ${entry.contingencies.join(', ')}`}
                  </div>
                  {entry.notes && (
                    <div style={{ fontSize: 11, color: theme.colors.text, marginTop: 4, fontStyle: 'italic' }}>
                      "{entry.notes}"
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Deals Panel ──

function DealsPanel({ lead }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch('/api/deals', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const data = await r.json();
        if (r.ok) {
          setDeals((data.deals || []).filter(d => d.contact_id === lead.id || d.buyer_id === lead.id || d.seller_id === lead.id));
        }
      } catch {} finally { setLoading(false); }
    })();
  }, [lead.id]);

  if (loading) return <div style={{ textAlign: 'center', padding: 16, color: theme.colors.textLight, fontSize: 12 }}><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /></div>;

  if (!deals.length) {
    const ls = (lead.lead_status || '').toLowerCase();
    const isCommercial = lead.lead_type === 'commercial';
    const triggerStage = isCommercial ? 'LOI Accepted' : 'Offer Accepted';
    const earlyStages = ['new', 'contacted', 'qualified', 'nurturing'];
    const isEarly = earlyStages.includes(ls);

    return (
      <div style={{ textAlign: 'center', padding: 20 }}>
        <Briefcase size={20} style={{ color: theme.colors.primary, marginBottom: 8, opacity: 0.6 }} />
        <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 6 }}>
          No deals yet
        </div>
        <div style={{ fontSize: 11, color: theme.colors.textLight, lineHeight: 1.5 }}>
          {isEarly ? (
            <>Move this lead to<br /><strong style={{ color: theme.colors.text }}>{triggerStage}</strong><br />to auto-create a deal in Transactions.</>
          ) : ls === 'under_contract' ? (
            <>This lead is under contract.<br />A deal should appear once synced —<br />check the <strong style={{ color: theme.colors.text }}>Transactions</strong> tab.</>
          ) : ls === 'closed' ? (
            <>This lead is closed. Past deals<br />may have been archived.</>
          ) : (
            <>Move this lead to<br /><strong style={{ color: theme.colors.text }}>{triggerStage}</strong><br />to create a deal.</>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {deals.map(d => (
        <div key={d.id} style={{
          padding: '8px 10px', marginBottom: 6, borderRadius: 8,
          border: `1px solid ${theme.colors.border}`, background: theme.colors.bg,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text }}>{d.address || d.property_address || 'Untitled Deal'}</div>
          <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
            {d.status && <span style={{ textTransform: 'capitalize' }}>{d.status}</span>}
            {d.contract_price && <span> · ${(d.contract_price / 1000).toFixed(0)}K</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Action Plans Panel (with Brain Recommendations) ──

function matchPlanToLead(template, lead) {
  const reasons = [];
  const staleness = stalenessOf(lead);
  const lt = (lead.lead_type || '').toLowerCase();
  const ls = (lead.lead_status || '').toLowerCase();
  const cat = (template.category || 'general').toLowerCase();
  const cst = (lead.commercial_sub_type || '').toLowerCase();

  // Category match
  if (cat === 'buyer' && (lt === 'buyer' || cst === 'buyer_rep' || cst === 'tenant_rep'))
    reasons.push('Buyer lead');
  if (cat === 'seller' && (lt === 'seller' || cst === 'seller_rep' || cst === 'landlord_rep'))
    reasons.push('Seller lead');
  if (cat === 'past_client' && ls === 'closed')
    reasons.push('Past client');
  if (cat === 'sphere' && (ls === 'nurturing' || lead.lead_source === 'sphere' || lead.lead_source === 'referral'))
    reasons.push('Sphere / nurture');

  // Staleness signal — cold leads benefit from re-engagement plans
  if (staleness === 'cold' && (cat === 'general' || reasons.length > 0))
    reasons.push('No contact in 14+ days');
  if (staleness === 'cool' && (cat === 'general' || reasons.length > 0))
    reasons.push('Cooling off (7-14 days)');

  // Stage match via trigger_filter
  const tf = template.trigger_filter || {};
  if (tf.lead_status && tf.lead_status === ls)
    reasons.push(`Stage: ${ls}`);
  if (tf.lead_type && tf.lead_type === lt)
    reasons.push(`Type: ${lt}`);

  // General plans get a weaker match if nothing else matched
  if (cat === 'general' && reasons.length === 0 && ls !== 'closed' && ls !== 'lost')
    reasons.push('General plan');

  return reasons;
}

function PlansPanel({ lead, showToast }) {
  const [enrollments, setEnrollments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrollingId, setEnrollingId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const token = session.access_token;

      // Fetch enrollments + templates in parallel
      const [enrollRes, tmplRes] = await Promise.all([
        supabase
          .from('action_plan_enrollments')
          .select('id, status, enrolled_at, current_step, current_step_order, template_id, action_plan_templates(id, name, description, category, step_count)')
          .eq('contact_id', lead.id)
          .order('enrolled_at', { ascending: false }),
        fetch('/api/action-plans', {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.json()),
      ]);

      setEnrollments(enrollRes.data || []);
      setTemplates(tmplRes.templates || []);
    } catch (e) {
      console.error('PlansPanel fetch error:', e);
    } finally { setLoading(false); }
  }, [lead.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEnroll = async (template) => {
    setEnrollingId(template.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/action-plans?enroll=1', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: template.id, contact_id: lead.id }),
      });
      const data = await r.json();
      if (r.ok) {
        showToast?.(`Enrolled in "${template.name}"`, 'success');
        fetchData();
      } else {
        showToast?.(data.error || 'Failed to enroll', 'error');
      }
    } catch {
      showToast?.('Error enrolling in plan', 'error');
    } finally { setEnrollingId(null); }
  };

  const handlePauseResume = async (enrollment) => {
    const newStatus = enrollment.status === 'paused' ? 'active' : 'paused';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`/api/action-plans?enrollment_id=${enrollment.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      showToast?.(`Plan ${newStatus === 'paused' ? 'paused' : 'resumed'}`, 'success');
      fetchData();
    } catch {
      showToast?.('Error updating plan', 'error');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 16, color: theme.colors.textLight, fontSize: 12 }}><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /></div>;

  // Build recommendations: templates not already enrolled, matched to lead signals
  const enrolledIds = new Set(enrollments.map(e => e.template_id));
  const scored = templates
    .filter(t => t.is_active && !enrolledIds.has(t.id))
    .map(t => ({ ...t, reasons: matchPlanToLead(t, lead) }))
    .filter(t => t.reasons.length > 0)
    .sort((a, b) => b.reasons.length - a.reasons.length);
  const recommended = showAll ? scored : scored.slice(0, 3);
  const hasActive = enrollments.some(e => e.status === 'active');

  const STATUS_COLORS = {
    active: '#22c55e', paused: '#eab308', completed: '#3b82f6', cancelled: '#ef4444',
  };

  return (
    <div>
      {/* ── Enrolled Plans ── */}
      {enrollments.length > 0 && (
        <div style={{ marginBottom: recommended.length > 0 ? 14 : 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: theme.colors.textLight, marginBottom: 6, padding: '0 2px' }}>
            Enrolled
          </div>
          {enrollments.map(e => {
            const plan = e.action_plan_templates;
            const sc = STATUS_COLORS[e.status] || theme.colors.textLight;
            return (
              <div key={e.id} style={{
                padding: '8px 10px', marginBottom: 6, borderRadius: 8,
                border: `1px solid ${theme.colors.border}`, background: theme.colors.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {plan?.name || 'Unknown Plan'}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: sc }} />
                    <span style={{ textTransform: 'capitalize' }}>{e.status}</span>
                    {plan?.step_count && <span>· Step {e.current_step_order || e.current_step || 1}/{plan.step_count}</span>}
                  </div>
                </div>
                {(e.status === 'active' || e.status === 'paused') && (
                  <button
                    onClick={() => handlePauseResume(e)}
                    title={e.status === 'paused' ? 'Resume' : 'Pause'}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer', padding: 4,
                      color: e.status === 'paused' ? '#22c55e' : '#eab308',
                    }}
                  >
                    {e.status === 'paused' ? <Play size={13} /> : <Pause size={13} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Recommended Plans ── */}
      {recommended.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#8b5cf6', marginBottom: 6, padding: '0 2px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Sparkles size={10} /> Recommended
          </div>
          {recommended.map(t => (
            <div key={t.id} style={{
              padding: '8px 10px', marginBottom: 6, borderRadius: 8,
              border: `1px solid #8b5cf620`, background: '#8b5cf608',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.name}
                  </div>
                  {t.description && (
                    <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2, lineHeight: 1.4 }}>
                      {t.description.length > 80 ? t.description.slice(0, 80) + '…' : t.description}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {t.reasons.map((r, i) => (
                      <span key={i} style={{
                        fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 4,
                        background: '#8b5cf618', color: '#8b5cf6',
                      }}>{r}</span>
                    ))}
                    {t.step_count > 0 && (
                      <span style={{
                        fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 4,
                        background: `${theme.colors.primary}15`, color: theme.colors.primary,
                      }}>{t.step_count} steps</span>
                    )}
                  </div>
                </div>
                <button
                  disabled={enrollingId === t.id}
                  onClick={() => handleEnroll(t)}
                  style={{
                    padding: '5px 10px', borderRadius: 6, border: 'none',
                    background: '#8b5cf6', color: 'white',
                    fontSize: 10, fontWeight: 700, cursor: enrollingId === t.id ? 'not-allowed' : 'pointer',
                    opacity: enrollingId === t.id ? 0.6 : 1,
                    whiteSpace: 'nowrap', flexShrink: 0, marginTop: 1,
                  }}
                >
                  {enrollingId === t.id ? '…' : 'Enroll'}
                </button>
              </div>
            </div>
          ))}
          {scored.length > 3 && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              style={{
                width: '100%', padding: '6px 0', border: 'none', borderRadius: 6,
                background: 'transparent', color: '#8b5cf6', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', marginTop: 2,
              }}
            >
              Show {scored.length - 3} more
            </button>
          )}
        </div>
      )}

      {/* ── Empty state ── */}
      {enrollments.length === 0 && recommended.length === 0 && (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Sparkles size={20} style={{ color: '#8b5cf6', marginBottom: 8 }} />
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 4 }}>
            No action plans yet
          </div>
          <div style={{ fontSize: 11, color: theme.colors.textLight, lineHeight: 1.5 }}>
            Create plan templates in<br />
            <strong style={{ color: theme.colors.text }}>CRM → Automation tab</strong><br />
            to see recommendations here.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Relationship Component ──

function AddRelationship({ contactId, orgId, onDone, showToast }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [relType, setRelType] = useState('spouse');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('contacts')
          .select('id, first_name, last_name')
          .eq('org_id', orgId)
          .neq('id', contactId)
          .or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%`)
          .limit(5);
        setResults(data || []);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [search, orgId, contactId]);

  const link = async (relatedId) => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      await supabase.from('contact_relationships').insert({
        org_id: orgId,
        contact_id: contactId,
        related_id: relatedId,
        relationship: relType,
        created_by: session.user.id,
      });
      showToast?.('Relationship added');
      onDone();
    } catch (e) { showToast?.(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 6, padding: 8, borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.bg }}>
      <select value={relType} onChange={e => setRelType(e.target.value)} style={{ ...selectStyle(), marginBottom: 6, width: '100%' }}>
        {RELATIONSHIP_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
      <input
        value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search contacts…"
        style={{ ...inputStyle(), width: '100%', boxSizing: 'border-box', marginBottom: 4 }}
      />
      {results.map(r => (
        <button key={r.id} onClick={() => link(r.id)} disabled={saving} style={{
          display: 'block', width: '100%', textAlign: 'left',
          padding: '5px 8px', border: 'none', background: 'transparent',
          cursor: 'pointer', fontSize: 12, color: theme.colors.text, borderRadius: 4,
        }}
          onMouseEnter={e => e.currentTarget.style.background = theme.colors.card}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {r.first_name} {r.last_name}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Shared Primitives
// ══════════════════════════════════════════════════════════════

function timelineIconFor(kind) {
  const map = {
    note:           { icon: FileText,      label: 'Note',           color: theme.colors.info },
    task_created:   { icon: Plus,          label: 'Task created',   color: theme.colors.primary },
    task_completed: { icon: CheckCircle2,  label: 'Task completed', color: theme.colors.success },
    lead_created:   { icon: UserPlus,      label: 'Lead added',     color: theme.colors.accent },
    status_marker:  { icon: ArrowRight,    label: 'Status',         color: theme.colors.textLight },
    message:        { icon: MessageSquare, label: 'Message',        color: '#3B82F6' },
    call:           { icon: Phone,         label: 'Call',           color: '#16A34A' },
    email_sent:     { icon: Mail,          label: 'Email',          color: '#8B5CF6' },
  };
  return map[kind] || { icon: FileText, label: kind, color: theme.colors.textLight };
}

function highlightMentions(text) {
  if (!text.includes('@')) return text;
  const parts = text.split(/(@\w[\w\s]*?\b)/g);
  return parts.map((part, i) =>
    part.startsWith('@')
      ? <span key={i} style={{ color: theme.colors.primary, fontWeight: 600 }}>{part}</span>
      : part
  );
}

function ActionButton({ icon, label, color, disabled, active, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
      padding: '8px 6px', borderRadius: 8, fontSize: 11, fontWeight: 600,
      border: active ? `1.5px solid ${color}` : `1px solid ${theme.colors.border}`,
      background: active ? `${color}10` : theme.colors.card,
      color: disabled ? theme.colors.textLight : color,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1, transition: 'all 0.15s',
    }}>
      {icon} {label}
    </button>
  );
}

const SECTION_COLORS = {
  'Lead Status': { color: '#6366F1', icon: '📊' },
  'Contact': { color: '#3B82F6', icon: '👤' },
  'Pipeline': { color: '#8B5CF6', icon: '🔄' },
  'Lender': { color: '#10B981', icon: '🏦' },
  'Tags': { color: '#F59E0B', icon: '🏷️' },
  'Relationships': { color: '#06B6D4', icon: '🔗' },
  'Quick Notes': { color: '#64748B', icon: '📝' },
  'Tasks': { color: '#EF4444', icon: '✅' },
  'Deals': { color: '#16A34A', icon: '🏠' },
  'Action Plans': { color: '#8B5CF6', icon: '⚡' },
  'Web Activity': { color: '#3B82F6', icon: '🌐' },
  'Commercial Info': { color: '#8B5CF6', icon: '🏢' },
};

function Section({ title, children }) {
  const meta = SECTION_COLORS[title] || { color: theme.colors.textLight, icon: '' };
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 10px', marginBottom: 8, borderRadius: 6,
        background: `${meta.color}08`,
        borderLeft: `3px solid ${meta.color}`,
      }}>
        {meta.icon && <span style={{ fontSize: 12 }}>{meta.icon}</span>}
        <span style={{
          fontSize: 10, fontWeight: 700, color: meta.color,
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function InfoField({ label, value, field, readOnly, editing, draft, setDraft, startEdit, cancelEdit, commit, type = 'text' }) {
  if (readOnly) {
    return (
      <div style={fieldRow()}>
        <div style={fieldLabel()}>{label}</div>
        <div style={{ flex: 1, color: theme.colors.text, fontSize: 12 }}>{value || '—'}</div>
      </div>
    );
  }
  if (!editing?.[field]) {
    return (
      <div style={fieldRow()}>
        <div style={fieldLabel()}>{label}</div>
        <button onClick={() => startEdit(field)} style={{
          flex: 1, textAlign: 'left', padding: '4px 6px', borderRadius: 4,
          background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
          fontSize: 12, color: theme.colors.text,
        }}
          onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {value || <span style={{ color: theme.colors.textLight }}>—</span>}
        </button>
      </div>
    );
  }
  return (
    <div style={fieldRow()}>
      <div style={fieldLabel()}>{label}</div>
      <input autoFocus type={type} value={draft?.[field] ?? ''}
        onChange={e => setDraft(d => ({ ...d, [field]: e.target.value }))}
        onBlur={() => commit(field)}
        onKeyDown={e => { if (e.key === 'Enter') commit(field); if (e.key === 'Escape') cancelEdit(field); }}
        style={{ ...inputStyle(), flex: 1, fontSize: 12, padding: '4px 6px' }}
      />
    </div>
  );
}

function TagEditor({ tags, onSave }) {
  const [adding, setAdding] = useState(false);
  const [newTag, setNewTag] = useState('');

  const add = () => {
    const t = newTag.trim();
    if (!t || tags.includes(t)) { setNewTag(''); setAdding(false); return; }
    onSave([...tags, t]);
    setNewTag(''); setAdding(false);
  };

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {tags.map(t => (
        <span key={t} style={{
          display: 'inline-flex', alignItems: 'center', gap: 2,
          padding: '2px 7px', borderRadius: 8,
          background: `${theme.colors.primary}15`, color: theme.colors.primary,
          fontSize: 10, fontWeight: 600,
        }}>
          {t}
          <button onClick={() => onSave(tags.filter(x => x !== t))} style={{
            background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0,
          }}><X size={8} /></button>
        </span>
      ))}
      {adding ? (
        <input autoFocus value={newTag} onChange={e => setNewTag(e.target.value)}
          onBlur={add} onKeyDown={e => { if (e.key === 'Enter') add(); if (e.key === 'Escape') setAdding(false); }}
          style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, width: 60 }}
        />
      ) : (
        <button onClick={() => setAdding(true)} style={{
          padding: '2px 6px', borderRadius: 8, border: `1px dashed ${theme.colors.border}`,
          background: 'transparent', cursor: 'pointer', fontSize: 10, color: theme.colors.textLight,
        }}>+ tag</button>
      )}
    </div>
  );
}

function ScoreChip({ score }) {
  const color = score >= 75 ? theme.colors.success : score >= 50 ? theme.colors.warning : theme.colors.danger;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '1px 6px', borderRadius: 6,
      background: `${color}18`, color, fontSize: 10, fontWeight: 800,
    }}>{score}</span>
  );
}

function StageBadge({ statusMeta }) {
  return (
    <span style={{
      display: 'inline-flex', padding: '1px 6px', borderRadius: 5,
      fontSize: 10, fontWeight: 700, background: `${statusMeta.color}18`, color: statusMeta.color,
    }}>{statusMeta.label}</span>
  );
}

function StalenessBadge({ staleness, days }) {
  const meta = {
    fresh: { color: theme.colors.success, label: `${days ?? 0}d` },
    warm:  { color: theme.colors.info,    label: `${days ?? 0}d` },
    cool:  { color: theme.colors.warning, label: `${days ?? 0}d` },
    cold:  { color: theme.colors.danger,  label: `${days ?? '—'}d` },
  }[staleness] || { color: theme.colors.textLight, label: '—' };
  return (
    <span style={{
      display: 'inline-flex', padding: '1px 5px', borderRadius: 5,
      fontSize: 9, fontWeight: 700, background: `${meta.color}18`, color: meta.color,
    }}>{meta.label}</span>
  );
}

// ── Style helpers ──

function fieldRow() {
  return { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 };
}

function fieldLabel() {
  return { width: 60, fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 };
}

function inputStyle() {
  return {
    padding: '6px 8px', borderRadius: 6,
    border: `1px solid ${theme.colors.border}`,
    fontSize: 12, color: theme.colors.text, background: theme.colors.card,
  };
}

function selectStyle() {
  return {
    ...inputStyle(), cursor: 'pointer',
  };
}

function btnPrimary() {
  return {
    padding: '8px 12px', borderRadius: 8, border: 'none',
    background: theme.colors.primary, color: 'white',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  };
}


